<template>
    <div class="stretch-layout">
        <card xs-tile sm-tile class="flex-grow-1">
            <card-text>
				<div v-if="pageStyle.content" class="border-b pb-16 mb-16">
					<user-content :text="pageStyle.content" />
				</div>
				<form-wizard v-if="products.length" v-model="activeStepIndex" :action="$api('events.purchases.store', {event: eventStore.event._key})" :data="submitData" :steps="wizardSteps"
							 :color="eventStore.colorStyle"
							 v-model:loading="isLoading"
							 persist-success-loading
							 @success="handleSuccessfulPurchase" @error="handleError">

					<template v-slot:shop>
						<event-product-purchase-form-inputs :event="eventStore.event" :products="products"
															:item-list-id="analyticsItemListId"
															:item-list-name="analyticsItemListName"
															:data="selectedProductsData"/>
						<validation-input :model-value="!!Object.keys(selectedProductsData).length" :error="$t('validation.required_product_selection')"/>
					</template>

					<template v-slot:data>
						<div v-if="!$auth.loggedIn" class="mt-8">
							<row>
								<column cols="12" sm="6">
									<form-input v-model="purchaseData.first_name"
												:rules="{required: true, max: 50, min: 2}"
												:disabled="isLoading"
												:label="$t('label.your_first_name')"/>
								</column>
								<column cols="12" sm="6">
									<form-input v-model="purchaseData.last_name"
												:rules="{required: true, max: 50, min: 2}"
												:disabled="isLoading"
												:label="$t('label.your_last_name')"/>
								</column>
								<column cols="12">
									<form-input v-model="purchaseData.email"
												type="email"
												:rules="{required: true, max: 150, min: 4}"
												:disabled="isLoading"
												:help="$t('help.product.purchase.email')"
												:label="$t('label.your_email')"/>
								</column>
							</row>
						</div>

						<template v-if="requiresDeliveryData">
							<divider v-if="!$auth.loggedIn" margin="16" />

							<p class="subheading">{{ $t('label.delivery_address') }}</p>
							<p class="subheading-description">{{ $t('text.event_product_purchase.delivery_address') }}</p>

							<delivery-data-form-inputs :data="deliveryData" required :disabled="isLoading"/>
						</template>

					</template>

					<template v-slot:checkout>
						<row>
							<column v-if="totalPrice > 0" cols="12" lg="7">
								<p class="subheading">{{ $t('label.billing_address') }}</p>
								<p class="subheading-description">{{ $t('text.order.billing_address') }}</p>
								<billing-form-inputs :data="billingAddress" required :disabled="isLoading"/>
							</column>
							<column cols="12" :lg="totalPrice > 0 ? 5 : 12">
								<p class="subheading">{{ $t('label.summary') }}</p>
								<p class="subheading-description">{{ $t('text.order.summary') }} {{ isFree ? '' : $t('text.order.checkout_coupon_info') }}</p>

								<expansion-panels>
									<template v-for="selectedProductData in selectedProductsData" :key="`product-${selectedProductData.product_id}`">
										<expansion-panel>
											<template #header>
												<div class="flex justify-between items-center w-full">
	<span class="font-regular leading-spaced" :class="{'text-primary': !eventStore.colorStyle}" :style="{color: eventStore.colorStyle}">
                                                            {{ selectedProductData.product.name }}{{
			selectedProductData.quantity > 1 ? ` (x${selectedProductData.quantity})` : '' }}</span>
													<div class="mx-8 text-success">
														{{ toEuro((selectedProductData.activePrice ? (selectedProductData.activePrice.cents * selectedProductData.quantity) : 0), true, false, false)
														}}
													</div>
												</div>
											</template>

											<alert v-if="selectedProductData.product.action" small>{{
													$t(`enum.event_product_action.${selectedProductData.product.action}.help`)
												}}
											</alert>

											<description-list
												v-if="selectedProductData.product.inputs.length && selectedProductData.inputs && Object.keys(selectedProductData.inputs).length"
												small>
												<template v-for="(inputs, inputsIndex) in selectedProductData.inputs">
													<b v-if="selectedProductData.inputs.length > 1">{{ selectedProductData.product.name }} {{ inputsIndex + 1}}:</b>
													<description-list-item v-for="(input, inputKey) in Object.values(inputs)" :key="`input-${inputsIndex}-${inputKey}`"
																		   :title="input.label">
														<template #text>
															<dynamic-input-value :input="input" :value="input.value"/>
														</template>
													</description-list-item>
												</template>
											</description-list>
										</expansion-panel>
									</template>
								</expansion-panels>

								<divider margin="16"/>

								<div class="flex justify-between items-center mt-24 mb-20">
									<div class="flex flex-column">
										<span class="text-md font-bold text-success">{{ $t('label.total_price') }}</span>
										<span class="text-4xs text-soft leading-tight">{{ $t('text.including_fees') }}</span>
									</div>
									<span class="text-xl font-bold text-success">{{ toEuro(totalPrice, true, false, false) }}</span>
								</div>
							</column>
						</row>
						<form-checkbox v-model="gtcAccepted" :rules="{accepted: true}" :label="$t('input.event_purchase.gtc')">
							<div class="flex items-center">
								<a href="#" @click.prevent="globalStore.showGtcModal">{{ $t('action.open_app_gtc') }}</a>
								<span class="px-8">/</span>
								<a href="#" @click.prevent="showEventGtcModal = true">{{ $t('action.open_event_gtc') }}</a>
							</div>
						</form-checkbox>
						<modal v-model="showEventGtcModal" :title="$t('label.event_gtc')">
							<server-content :action="$api('events.gtc', eventStore.event.id)"
											as-guest
											:data="{ts: eventStore.event.cache_buster}"
											:missing="$t('missing.event.gtc')"
											access="gtc" skeleton="paragraph@30"/>
						</modal>
					</template>

				</form-wizard>
				<missing-info v-else :text="$t('missing.event.shop_products')"/>
            </card-text>
        </card>
    </div>
</template>

<script setup lang="ts">
import {EventPageType, EventProductPurchaseLocation, getAnalyticsDataForEventProduct, ModelType, toEuro} from "@spoferan/spoferan-ts-core";
import {useEventStore} from "../../../store/event";
import {useGlobalStore} from "@spoferan/nuxt-spoferan/store";

const eventStore = useEventStore();
const globalStore = useGlobalStore();
const {t} = useI18n();
const {$api, $web, $date, $auth, $analytics} = useNuxtApp();
const route = useRoute();
const router = useRouter();

let pageStyle = {};
let products = [];

// Make sure the page is activated
const page = eventStore.getPage(EventPageType.Shop);
if (!page) {
	await navigateTo($web('events.show', route.params.event));
} else {
	const responses = await Promise.all([
		useApiFetch($api('events.pages.show', [eventStore.event.id, page.id]), {
			guest: true,
			params: {
				ts: page.cache_buster,
			}
		}),
		useApiFetch($api('events.products.index', [eventStore.event.id]), {
			guest: true,
			params: {
				only_visible: true,
				relations: ['prices', 'cancelConditions', 'inputs.selectOptions'],
				purchase_location: [EventProductPurchaseLocation.Shop, EventProductPurchaseLocation.ParticipationAndShop]
			}
		})
	]);

	pageStyle = responses[0].data;
	products = responses[1].data;
}

const analyticsItemListId = `event-shop-${eventStore.event.id}`;
const analyticsItemListName = `Event Shop: ${eventStore.event.display_name}`;

const selectedProductsData = ref({});
const purchaseData = ref({});

// The wizard step currently visible to the user
const activeStepIndex = ref(1);

const gtcAccepted = ref(false);
const showEventGtcModal = ref(false);
const didInputData = ref(false);
useLeavePageWarning(didInputData);
const isLoading = ref(false);

// Whether the delivery data form shall be shown, i.e., a selected event product requires a delivery address.
const requiresDeliveryData = computed(() => {
	return Object.values(selectedProductsData.value).some(data => data.product.requires_address);
});

const wizardSteps = computed(() => {
	const steps: {[key: string]: any}[] = [{
		name: 'shop',
		title: t('action.select_products'),
		editable: true,
		next: !$auth.loggedIn || requiresDeliveryData.value ? t('action.next') : t('action.to_checkout')
	}];

	if (!$auth.loggedIn || requiresDeliveryData.value) {
		steps.push({
			name: 'data',
			title: t('label.purchase_data'),
			editable: true,
			next: t('action.to_checkout')
		})
	}

	steps.push({
		name: 'checkout',
		title: t('label.checkout'),
		next: isFree.value ? t('action.book_now') : t('action.book_and_pay')
	})
	return steps
});

// An array of objects including the amount and model type to pay
const orderStatements = computed(() => {
	const statements = [];
	Object.values(selectedProductsData.value).forEach(productData => {
		if (productData.activePrice) {
			statements.push({
				type: ModelType.EventProductPurchase,
				amount: (productData.quantity * productData.activePrice.cents)
			});
		}
	})

	return statements
});

const {
	billingAddress,
	deliveryData,
	totalPrice,
	isFree,
	initializeDeliveryData,
	initializeBillingAddress
} = useCheckoutData(orderStatements)

// The data to send to the server
const submitData = computed(() => {

	const data = {
		...purchaseData.value,
		products: [],
		gtc: gtcAccepted.value,
		delivery: deliveryData.value
	}

	if (totalPrice.value > 0) {
		data.billing = billingAddress.value
	}

	// Add the product purchases
	Object.values(selectedProductsData.value).forEach(productData => {
		data.products.push({
			statement_id: `product_${productData.product_id}`,
			product_id: productData.product_id,
			quantity: productData.quantity,
			expected_price_id: productData.activePrice ? productData.activePrice.id : null,
			inputs: productData.inputs
		})
	})

	return data
});

watch(activeStepIndex, (newStep) => {
	// Prefill the delivery data with suggested data
	// We need to do this in here as only when switching to the delivery step all suggest data is available
	if (requiresDeliveryData.value && newStep === wizardSteps.value.length - 1) {
		initializeDeliveryData()
	}

	// If we are on the last step, we initialize the checkout data based on the purchase data
	if (newStep === wizardSteps.value.length) {
		initializeBillingAddress(Object.keys(purchaseData.value).length ? {
			name: `${purchaseData.value.first_name} ${purchaseData.value.last_name}`,
			email: purchaseData.value.email
		} : {});
	}
});

watch(selectedProductsData, () => {
	didInputData.value = true;
});

watch(deliveryData, () => {
	didInputData.value = true;
});

watch(requiresDeliveryData, (requiresDeliveryData) => {
	if (!requiresDeliveryData) {
		deliveryData.value = {};
	}
});

function handleSuccessfulPurchase(order) {
	didInputData.value = false; // Allows to leave the page without warnings

	$analytics.trackEvent('begin_checkout', {
		item_list_id: analyticsItemListId,
		item_list_name: analyticsItemListName,
		currency: 'EUR',
		value: (Math.round((totalPrice.value / 100) * 100) / 100),
		items: Object.values(selectedProductsData.value).map((productData, productIndex) => getAnalyticsDataForEventProduct(eventStore.event, productData.product, productData.quantity, productData.activePrice ? productData.activePrice.cents : null, productIndex))
	});

	// Check if the user needs to pay the order
	if (order.requires_payment) {
		router.push($web('orders.pay', [order._key]))
	} else {
		router.push({
			path: $web('orders.show', [order._key]),
			query: {success_type: 'product_purchase'}
		});
	}
}

function handleError(status, response) {
	const errorCode = response.error_code ? response.error_code : 'unknown';
	switch (errorCode) {
		// Refresh the active price if the expected one is not valid anymore
		case 'unexpected_active_price':
			const statementId = response.data.statement_id;
			const activePrice = response.data.activePrice;
			Object.values(selectedProductsData.value).forEach(productData => {
				const currentParticipationStatementId = `product_${productData.product_id}`;
				if (currentParticipationStatementId === statementId) {
					productData.activePrice = activePrice;
				}
			})
			break;
	}
}

useMeta({
	title: eventStore.buildPageTitle(t('view.events.shop.index.title', {event: eventStore.event.display_name})),
	description: t('view.events.shop.index.description', {event: eventStore.event.display_name})
})

</script>

